import { PInlineNotification, PLinkPure, PTextList, PTextListItem } from "@porsche-design-system/components-react"
import type { PublishingRuleKey, PublishingRuleViolation, PublishingRuleViolationsView } from "@slfinrtl/ui-bff-routes"
import { defineMessages, useIntl } from "react-intl"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import type { DealerAppRoute } from "src/common/routes/route"
import type { DealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import type { VehicleDetailsStage } from "../../common/VehicleDetailsStage"
import { VehicleDetailsStageLink } from "../../common/VehicleDetailsStageLink"

interface PublishingRuleViolationsProps {
	inventoryId: string
	view: PublishingRuleViolationsView
}

interface DialogProps {
	canEditImages?: boolean
	canEditPrice?: boolean
	canEditStocknumber?: boolean
	heading: string
	inventoryId: string
	state: "warning" | "error"
	violations: PublishingRuleViolation[]
}

const messages = defineMessages({
	contact: {
		defaultMessage: "Please get in touch with your market representative",
		id: "vehicleDetails.publishingRuleViolations.error.contact",
	},
	errorHeading: {
		defaultMessage: "Please complete all the required information so that the listing can be created:",
		id: "vehicleDetails.publishingRuleViolations.error.heading",
	},
	warningHeading: {
		defaultMessage: "Please complete the following information so that the listing can be published:",
		id: "vehicleDetails.publishingRuleViolations.warning.heading",
	},
})

export const PublishingRuleViolations = (props: PublishingRuleViolationsProps) => {
	const { inventoryId, view } = props
	const { formatMessage } = useIntl()

	return (
		<>
			{view.errors.length > 0 && (
				<PublishingRuleViolationsDialog
					state="error"
					heading={formatMessage(messages.errorHeading)}
					violations={view.errors}
					inventoryId={inventoryId}
				/>
			)}
			{view.warnings.length > 0 && (
				<PublishingRuleViolationsDialog
					state="warning"
					heading={formatMessage(messages.warningHeading)}
					violations={view.warnings}
					inventoryId={inventoryId}
					canEditImages={view.canEditImages}
					canEditPrice={view.canEditPrice}
					canEditStocknumber={view.canEditStocknumber}
				/>
			)}
		</>
	)
}

const canEdit = (
	sectionName: PublishingRuleKey,
	canEditImages?: boolean,
	canEditPrice?: boolean,
	canEditStocknumber?: boolean,
) => {
	const map: Partial<Record<PublishingRuleKey, boolean>> = {
		"four-images-required": canEditImages,
		"price-required": canEditPrice,
		"stock-number-or-commission-number-required": canEditStocknumber,
		"stock-number-required": canEditStocknumber,
	}

	return map[sectionName] ?? true
}

const canBeChangedByUser: PublishingRuleKey[] = [
	"seller-email-and-phone-required",
	"commercial-register-number-required",
	"registration-date-required",
	"price-breakdown-required-for-new-cars",
	"interior-color-required",
	"exterior-color-required",
	"rooftop-color-required",
	"vin-required",
	"v200-required",
]

const mapEditStageRoute = (
	key: PublishingRuleKey,
	routes: DealerAppRoutes | null,
): { editStage?: VehicleDetailsStage; routerUrl: DealerAppRoute["url"] } | undefined => {
	switch (key) {
		case "four-images-required":
			return routes?.inventoryList?.vehicleDetails
				? {
						editStage: undefined,
						routerUrl: routes.inventoryList.vehicleDetails.mediaEdit.url,
					}
				: undefined
		case "price-required":
			return routes?.inventoryList?.vehicleDetails
				? {
						editStage: "pricing",
						routerUrl: routes.inventoryList.vehicleDetails.url,
					}
				: undefined
		case "stock-number-or-commission-number-required":
			return routes?.inventoryList?.vehicleDetails
				? {
						editStage: "inventoryIntake",
						routerUrl: routes.inventoryList.vehicleDetails.url,
					}
				: undefined
		case "stock-number-required":
			return routes?.inventoryList?.vehicleDetails
				? {
						editStage: "inventoryIntake",
						routerUrl: routes.inventoryList.vehicleDetails.url,
					}
				: undefined
	}
}

const PublishingRuleViolationsDialog = (props: DialogProps) => {
	const { canEditImages, canEditPrice, canEditStocknumber, heading, inventoryId, state, violations } = props
	const { formatMessage } = useIntl()
	const routes = useDealerAppRoutes()

	return (
		<PInlineNotification state={state} heading={heading} dismissButton={false}>
			<PTextList>
				{violations?.map((rule) => {
					const route = mapEditStageRoute(rule.name, routes)
					if (
						rule.severity === "warning" &&
						route &&
						canEdit(rule.name, canEditImages, canEditPrice, canEditStocknumber)
					) {
						return (
							<PTextListItem key={rule.name}>
								<PLinkPure icon="none">
									<VehicleDetailsStageLink
										to={route.routerUrl(inventoryId)}
										state={{
											editStage: route.editStage,
											scrollInstantly: true,
										}}
									>
										{rule.message}
									</VehicleDetailsStageLink>
								</PLinkPure>
							</PTextListItem>
						)
					}
					return (
						<PTextListItem key={rule.name}>
							{rule.message}
							{rule.severity === "error" && !canBeChangedByUser.includes(rule.name)
								? `— ${formatMessage(messages.contact)}`
								: null}
						</PTextListItem>
					)
				})}
			</PTextList>
		</PInlineNotification>
	)
}
